import { BlockDefinition } from "@softr-io/blocks-sdk";
import { isPreCompiledBlock } from "./PreCompiledBlockDefinition";
import { DynamicBlock } from "./DynamicBlock";
import { memo, useMemo } from "react";
import { PreCompiledBlock } from "./PreCompiledBlock";
import { ChildBlockProps, ErrorBoundary } from "@softr-io/blocks-sdk/react";
import { useAppConstants } from "./AppConstantsContext";

type Props = Readonly<{
    definition: BlockDefinition;
    /**
     * Must contain all child blocks of this block.
     */
    childBlocks: Readonly<Record<BlockDefinition["id"], BlockDefinition>>;
    /**
     * true if this block is a direct child of the page,
     * false if it's a child of another container block.
     */
    topLevel: boolean;
    childBlockRootProps: ChildBlockProps["wrapperProps"] | null;
    index?: number;
    hiddenDueToPreviousBlockLoading?: boolean;
}>;

function UnmemoizedBlock(props: Props) {
    const { mode } = useAppConstants();
    // Fixed and sticky headers break studio's layout, so we disable them here.
    const definition = useMemo(
        () => (mode === "studio" ? disableFixedAndVerticalHeaderSettings(props.definition) : props.definition),
        [props.definition, mode],
    );
    return <BlockInner {...props} definition={definition} />;
}
export const Block = memo(UnmemoizedBlock);

function BlockInner(props: Props) {
    const { definition } = props;
    const { mode } = useAppConstants();
    if (isPreCompiledBlock(definition)) {
        // Pre compiled blocks are handled outside of react
        // See block-root.ts for more info
        return (
            <ErrorBoundary>
                <PreCompiledBlock
                    definition={definition}
                    topLevel={props.topLevel}
                    hiddenDueToPreviousBlockLoading={props.hiddenDueToPreviousBlockLoading}
                    // In the studio, we apply the index as a key to force the re-creation of pre-compiled blocks
                    // to ensure that they re-order properly.
                    // This is sort of a hack, but I can't be bothered to re-implement this logic
                    // for the 3 people who re-order v1 and v2 blocks in the studio :D

                    key={mode === "studio" ? props.index : undefined}
                />
            </ErrorBoundary>
        );
    } else {
        return (
            <ErrorBoundary>
                <DynamicBlock {...props} />
            </ErrorBoundary>
        );
    }
}

function disableFixedAndVerticalHeaderSettings(definition: BlockDefinition): BlockDefinition {
    if (definition.category !== "Header") {
        return definition;
    }
    if (!definition.elements.verticalEnabled && !definition.elements.sticky) {
        return definition;
    }
    return {
        ...definition,
        elements: {
            ...definition.elements,
            verticalEnabled: false,
            sticky: false,
        },
    };
}
